import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useEffect, useRef, useState } from "react";
import { Navigate, useParams } from "react-router";
import { useRuntimeHttpClient } from "@/features/runtime-session/hooks/use-runtime-http-client";
import { ApiError } from "@/shared/api/api-error";
import { Callout } from "@/shared/ui/callout";
import { LoadingState } from "@/shared/ui/loading-state";

type DownloadState = "preparing" | "started" | "not-found" | "error";

function downloadPath(reportId: string): string {
  return `/generated-reports/download/${encodeURIComponent(reportId)}`;
}

function getFilename(contentDisposition: string | null): string {
  const match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition ?? "");

  if (match?.[1]) {
    try {
      return decodeURIComponent(match[1]);
    } catch {
      // The browser can still download the report with the fallback filename.
    }
  }

  return "report.zip";
}

function startBrowserDownload(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  link.style.display = "none";
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

export function GeneratedReportDownloadEntryRoute() {
  const { reportId } = useParams();

  if (!reportId) {
    return <Navigate to="/deployments" replace />;
  }

  const reportPath = downloadPath(reportId);
  return <Navigate to={`/deployments?continue=${encodeURIComponent(reportPath)}`} replace />;
}

export function GeneratedReportDownloadRoute() {
  const { deploymentId, reportId } = useParams();
  const runtime = useRuntimeHttpClient();
  const downloadStarted = useRef(false);
  const [state, setState] = useState<DownloadState>("preparing");

  useEffect(() => {
    if (
      !deploymentId ||
      !reportId ||
      runtime.client === null ||
      runtime.deploymentId !== deploymentId ||
      downloadStarted.current
    ) {
      return;
    }

    downloadStarted.current = true;
    const controller = new AbortController();

    void runtime.client
      .getBinary(`/v1/generated-reports/${encodeURIComponent(reportId)}/download`, {
        signal: controller.signal,
      })
      .then((response) => {
        if (controller.signal.aborted) {
          return;
        }

        startBrowserDownload(response.blob, getFilename(response.contentDisposition));
        setState("started");
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) {
          return;
        }

        if (error instanceof ApiError && (error.statusCode === 403 || error.statusCode === 404)) {
          setState("not-found");
          return;
        }

        setState("error");
      });

    return () => controller.abort();
  }, [deploymentId, reportId, runtime.client, runtime.deploymentId]);

  if (state === "preparing") {
    return <LoadingState message="Making your report ready..." />;
  }

  return (
    <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", px: 2, py: 6 }}>
      <Container maxWidth="sm">
        <Card>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Stack spacing={2}>
              {state === "started" && (
                <>
                  <Typography variant="h5">Your report download has started</Typography>
                  <Typography color="text.secondary">
                    You can safely close this page once the download appears in your browser.
                  </Typography>
                </>
              )}
              {state === "not-found" && (
                <>
                  <Typography variant="h5">404 — Report not found</Typography>
                  <Typography color="text.secondary">
                    This report does not exist or is not available to your account.
                  </Typography>
                </>
              )}
              {state === "error" && (
                <Callout severity="error" title="Report download could not be started">
                  Please check your connection and try the report link again.
                </Callout>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
